import React, { ReactElement } from 'react';
import { observable } from 'mobx';
import { IGridLineManager, ICanvasSize } from './index.interface';
import { SLIDE_CM_TO_PX } from '@src/slide/infrastructure/module/independent/unit';
import AbsorbManager from '@src/slide/application/service/absorb-manager';
import { IAbsorbManager } from '@src/slide/application/service/absorb-manager/absorb-manager.interface';
import { IGuidesManager } from '@src/slide/application/service/guides-manager/index.interface';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { onSlideAppReady, SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import GridLine, { IBaseGridLineProps } from './components/grid-line';
import userLocalStorage, { LocalStorageKeys } from '@src/slide/infrastructure/module/independent/local-storage';

enum EDisplayGridLineLocalValue {
    show = 0,
    hide = 1,
}
const defaultGridSpace = 0.25;
const drawRange = 3;

interface IAllGridInfo {
    [key: string]: number;
}

interface IGridLineManagerProps {
    absorbManager: AbsorbManager;
}

export default class GridLineManager implements IGridLineManager {
    /**
     * 是否展示网格线
     */
    @observable public displayGridLine: boolean;
    @observable public isGridAlign: boolean;
    @observable public displayGridLineDialog = false;
    @observable public gridSpace = 0;
    public absorbPointX: Array<number> = [];
    public absorbPointY: Array<number> = [];
    private allGridInfo: IAllGridInfo;
    private slideWidth: number;
    private slideHeight: number;
    private slideLeft: number;
    private slideTop: number;
    private canvasWidth: number;
    private canvasHeight: number;
    private absorbManager: IAbsorbManager;
    private guidesManager?: IGuidesManager;

    public constructor(props: IGridLineManagerProps) {
        const { absorbManager } = props;
        this.absorbManager = absorbManager;
        // 获取缓存的网格显示信息
        const displayGuides = userLocalStorage.getItem(LocalStorageKeys.DISPLAY_GRID_LINE);
        if (displayGuides === EDisplayGridLineLocalValue.show) {
            this.displayGridLine = true;
        } else {
            this.displayGridLine = false;
        }

        // 获取缓存网格对齐信息
        const isGridAlign = userLocalStorage.getItem(LocalStorageKeys.USE_GRID_ALIGN);
        if (isGridAlign === EDisplayGridLineLocalValue.show) {
            this.isGridAlign = true;
        } else {
            this.isGridAlign = false;
        }
        this.absorbManager.setIsGridAbsorb(this.isGridAlign);

        onSlideAppReady(() => {
            this.getGridInfoByLocalStorage();
        });

        if (this.displayGridLine) {
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.GRID_LINE,
                action: TDWActionType.EXPOSURE_GRID_LINE,
            });
        }
    }

    public setGuidesManager(guidesManager: IGuidesManager) {
        this.guidesManager = guidesManager;
    }

    public setCanvasSize = (props: ICanvasSize) => {
        const { slideWidth, slideHeight, width, height, top, left, scale } = props;
        this.slideWidth = slideWidth;
        this.slideHeight = slideHeight;
        this.canvasWidth = width / scale;
        this.canvasHeight = height / scale;
        this.slideTop = top / scale;
        this.slideLeft = left / scale;
        this.setAbsorbPoint();
    };

    /**
     * 设置是否展示参考线，并缓存状态
     */
    public changeDisplayGridLine = (): void => {
        this.setDisplayGridLine(!this.displayGridLine);
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.GRID_LINE,
            action: TDWActionType.OPEN_GRID_LINE,
        });
    };

    /**
     * 设置网格对齐
     */
    public changeIsGridAlign = (): void => {
        this.setIsGridAlign(!this.isGridAlign);
    };

    public setGridSpace = (gridSpace: number): void => {
        try {
            this.gridSpace = gridSpace;
            this.allGridInfo[SlideDataServices.DocInfoManager()?.globalPadId ?? ''] = gridSpace;
            userLocalStorage.setItem(LocalStorageKeys.USE_GRID_INFO, this.allGridInfo);
            this.setAbsorbPoint();
        } catch (e) {
            window.log.error(`[grid-parse-error]${e}`);
        }
    };

    public setDisplayGridLine = (displayGridLine: boolean): void => {
        this.displayGridLine = displayGridLine;
        if (this.displayGridLine) {
            userLocalStorage.setItem(
                LocalStorageKeys.DISPLAY_GRID_LINE,
                EDisplayGridLineLocalValue.show,
            );
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.GRID_LINE,
                action: TDWActionType.EXPOSURE_GRID_LINE,
            });
        } else {
            userLocalStorage.setItem(
                LocalStorageKeys.DISPLAY_GRID_LINE,
                EDisplayGridLineLocalValue.hide,
            );
        }
    };

    public setIsGridAlign(isGridAlign: boolean): void {
        this.isGridAlign = isGridAlign;
        if (this.isGridAlign) {
            userLocalStorage.setItem(
                LocalStorageKeys.USE_GRID_ALIGN,
                EDisplayGridLineLocalValue.show,
            );
        } else {
            userLocalStorage.setItem(
                LocalStorageKeys.USE_GRID_ALIGN,
                EDisplayGridLineLocalValue.hide,
            );
        }
        this.absorbManager.setIsGridAbsorb(this.isGridAlign);
    }

    public getGridLineComponent(props: IBaseGridLineProps): ReactElement | null {
        if (!this.guidesManager) return null;
        return <GridLine gridLineManager={this} guidesManager={this.guidesManager} {...props}/>;
    }

    private getGridInfoByLocalStorage = (): void => {
        this.allGridInfo = userLocalStorage.getItem(
            LocalStorageKeys.USE_GRID_INFO,
            {},
        );

        this.gridSpace = this.allGridInfo[SlideDataServices.DocInfoManager()?.globalPadId ?? ''] || defaultGridSpace;
    };

    /**
     * 计算所有网格吸附点，左右上下都给一个吸附的冗余点
     */
    private setAbsorbPoint = () => {
        const absorbPointX: Array<number> = [];
        const absorbPointY: Array<number> = [];
        const cellLength = SLIDE_CM_TO_PX * this.gridSpace;
        const startXPosition = this.slideWidth / 2;
        const startYPosition = this.slideHeight / 2;

        for (let position = startXPosition; position > -this.slideLeft * 3; position = position - cellLength) {
            absorbPointX.push(position);
        }

        for (let position = startXPosition + cellLength;
            position < this.canvasWidth;
            position += cellLength) {
            absorbPointX.push(position);
        }

        for (let position = startYPosition; position > -this.slideTop * drawRange; position = position - cellLength) {
            absorbPointY.push(position);
        }

        for (let position = startYPosition + cellLength;
            position < this.canvasHeight;
            position += cellLength) {
            absorbPointY.push(position);
        }

        this.absorbManager.gridAbsorbLogic?.updateAbsorbPoint(absorbPointX, absorbPointY);
        this.absorbManager.gridAbsorbLogic?.updateAbsorbStep(this.gridSpace * SLIDE_CM_TO_PX);
        this.absorbPointX = absorbPointX;
        this.absorbPointY = absorbPointY;
    };
}
